/**
 * @fileoverview ユーザープロファイルページ
 *
 * このファイルでは、認証済みユーザーのプロファイル情報を表示する
 * ページコンポーネントを定義しています。ユーザー情報の表示や
 * アカウント設定へのアクセスを提供します。
 */

/**
 * ユーザープロファイルページコンポーネント
 *
 * ログインしているユーザーのプロファイル情報（ユーザー名、メールアドレス、
 * アカウント情報など）を表示します。また、パスワード変更などの
 * アカウント管理機能へのリンクも提供します。
 *
 * APIから取得したユーザー情報を表示し、必要に応じて情報の更新や
 * 編集機能へのアクセスを可能にします。
 *
 * @returns {JSX.Element} ユーザープロファイル表示画面
 */
import { useState, useEffect } from "react";
import { Button, Skeleton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Card } from "@/components/card";
import { PropertyListType, PropertyList } from "@/components/propertyList";
import { getUserInfo } from "@/api/getUserInfo";
import type { UserInfo } from "@/domain/userInfo";
import { useContext } from "react";
import { CsrfTokenContext } from "@/providers/csrfToken/context";

export default function UserPage() {
  const bffOrigin = import.meta.env.VITE_BFF_ORIGIN;
  const signoutUrl = `${bffOrigin}/v1/logout`;
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const csrfTokenContext = useContext(CsrfTokenContext);

  useEffect(() => {
    (async () => {
      const userInfo = await getUserInfo();
      setUserInfo(userInfo);
    })();
  }, []);

  const properties: PropertyListType = userInfo
    ? [
        { key: "ユーザID", value: userInfo.sub },
        { key: "ユーザ名", value: userInfo.name },
        { key: "メールアドレス", value: userInfo.email },
        {
          key: "パスワード",
          value: "********",
          link: { label: "変更", to: "/change-password" },
        },
      ]
    : [
        {
          key: "ユーザID",
          value: <Skeleton height={28} width="100%" radius="xl" />,
        },
        {
          key: "ユーザ名",
          value: <Skeleton height={28} width="100%" radius="xl" />,
        },
        {
          key: "メールアドレス",
          value: <Skeleton height={28} width="100%" radius="xl" />,
        },
        {
          key: "パスワード",
          value: <Skeleton height={28} width="100%" radius="xl" />,
        },
      ];

  const [logoutLoading, { open: setLogoutLoading }] = useDisclosure(false);

  return (
    <Card title="ユーザ情報">
      <PropertyList properties={properties} />
      <form
        id="logoutForm"
        action={signoutUrl}
        method="POST"
        onSubmit={() => setLogoutLoading()}
      >
        <input
          type="hidden"
          name="csrf_token"
          value={csrfTokenContext.csrfToken}
        />
        <Button
          type="submit"
          color="red"
          variant="outline"
          loaderProps={{ type: "dots" }}
          disabled={logoutLoading}
          loading={logoutLoading}
        >
          ログアウト
        </Button>
      </form>
    </Card>
  );
}
